import FormControl from '@mui/material/FormControl'
import { InputBase } from '@mui/material'
import { Text, Flex, Button } from 'components/elements'
import { styled, CSS } from 'theme'
import {
  ChangeEvent,
  KeyboardEvent,
  ReactElement,
  ReactNode,
  Ref,
  forwardRef,
  useState,
} from 'react'
import { FieldError } from 'react-hook-form'
import ElementTooltip from './Tooltip'
import { useTranslation } from 'react-i18next'

type InputFieldType = 'number' | 'text' | 'password'

export interface InputFieldProps {
  name: string
  css?: CSS
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
  placeholder: string
  label?: string
  preLabel?: boolean
  error?: FieldError | string
  disabled?: boolean
  editableInput?: boolean
  value?: string
  type?: InputFieldType
  textArea?: boolean
  iconElement?: {
    showIcon?: boolean
    iconPlacement?: string
    icon?: ReactNode
    toggleIcon?: ReactNode
    iconAsButton?: boolean
    switchable?: boolean
  }
  inputButton?: {
    end?: ReactNode
    start?: ReactNode
    endCss?: CSS
    startCss?: CSS
  }
  button?: {
    isEnabled: boolean
    buttonName: string
  }
  textAreaElement?: {
    row?: number
    col?: number
  }
  helperText?: {
    text?: string
    css?: CSS
  }
  maxLength?: number
  required?: boolean
  tooltip?: boolean
  inputProps?: { [key: string]: any }
  innerCSS?: CSS
}

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      name,
      css,
      type,
      onChange,
      onKeyDown,
      placeholder,
      value,
      label,
      preLabel,
      error,
      textArea = false,
      disabled = false,
      editableInput = false,
      iconElement = {
        showIcon: false,
        iconAsButton: false,
        iconPlacement: '',
        icon: '',
        toggleIcon: '',
        switchable: false,
      },
      inputButton = { end: '', start: '' },
      button = { isEnabled: false, buttonName: '' },
      textAreaElement = { row: 3, col: '' },
      helperText,
      maxLength,
      required = false,
      tooltip = false,
      inputProps,
      innerCSS,
    },
    ref: Ref<HTMLInputElement>
  ) => {
    const {
      showIcon,
      iconPlacement,
      icon,
      toggleIcon,
      iconAsButton,
      switchable,
    } = iconElement
    // Initialize state only if toggling is enabled (switchable true and toggleIcon provided)
    const [inputType, setInputType] = useState(
      toggleIcon && switchable ? 'password' : type
    )
    const [showPassword, setShowPassword] = useState(
      toggleIcon && switchable ? true : false
    )
    const [text, setText] = useState(value)

    // const disabledInput = editableInput || disabled

    const toggleText = () => {
      if (switchable) {
        setInputType(prevInputType =>
          prevInputType === type ? 'password' : type
        )
        setShowPassword(prevShowPassword => !prevShowPassword)
      }
    }

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target
      if (!maxLength || value.length <= maxLength) {
        setText(value)
        onChange?.(event)
      }
    }

    const currentIcon =
      toggleIcon && switchable ? (showPassword ? icon : toggleIcon) : icon

    const inputCss = {
      '&.MuiInputBase-root': {
        ...innerCSS,
      },
    }

    const { t } = useTranslation()

    return (
      <InputWrap
        variant="standard"
        css={{
          ...InputWrapCSS(css),
          ...(preLabel ? PrelabelTextCss : {}),
        }}
        disabled={disabled}
      >
        {label && (
          <Text as="label" htmlFor={name} css={{ textTransform: 'capitalize' }}>
            {label}{' '}
            {required ? (
              <Text css={{ color: '$error', ml: '$space$6' }}>*</Text>
            ) : null}
          </Text>
        )}
        <Input
          ref={ref}
          multiline={textArea}
          id={name}
          name={name}
          type={inputType}
          className={`${showIcon ? 'iconItem' : ''} `}
          placeholder={placeholder}
          value={value}
          error={!!error}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          inputProps={{
            maxLength,
            ...inputProps,
          }}
          disabled={disabled}
          minRows={textAreaElement?.row}
          css={{ ...inputCss }}
          startAdornment={
            <>
              {inputButton?.start ? (
                <Flex className="iconWrap" css={{ ...inputButton?.startCss }}>
                  {inputButton?.start}
                </Flex>
              ) : null}
              {iconPlacement === 'start' && showIcon ? (
                <Flex className="iconWrap">
                  {iconAsButton ? (
                    <Button
                      type="button"
                      onClick={toggleText}
                      transparent="asIconGray"
                      css={{
                        svg: {
                          path: {
                            stroke: '$gray400',
                          },
                        },
                      }}
                    >
                      {currentIcon}
                    </Button>
                  ) : (
                    currentIcon
                  )}
                </Flex>
              ) : null}
            </>
          }
          endAdornment={
            <>
              {button.isEnabled && (
                <Button type="button">{button.buttonName}</Button>
              )}
              {inputButton?.end ? (
                <Flex className="iconWrap" css={{ ...inputButton?.endCss }}>
                  {tooltip ? (
                    <ElementTooltip
                      children={inputButton?.end as ReactElement}
                      title={
                        <Text as="tooltip" color={'white'}>
                          This is {label}
                        </Text>
                      }
                      placement="top"
                    />
                  ) : (
                    <> {inputButton?.end}</>
                  )}
                </Flex>
              ) : null}

              {iconPlacement === 'end' && showIcon ? (
                <Flex className="iconWrap">
                  {iconAsButton ? (
                    <Button
                      type="button"
                      onClick={toggleText}
                      transparent="asIconGray"
                      css={{
                        svg: {
                          path: {
                            stroke: '$gray400',
                          },
                        },
                      }}
                    >
                      {currentIcon}
                    </Button>
                  ) : (
                    currentIcon
                  )}
                </Flex>
              ) : null}
            </>
          }
        />
        {!!error && (
          <Text color="error" className="errorField">
            {error as ReactNode}
          </Text>
        )}
        {helperText ? (
          <Text as="helperText" css={{ mt: '$space$6', ...helperText?.css }}>
            {helperText?.text}
            {maxLength !== undefined &&
              ` ${maxLength - (text ? text.length : 0)} ${t('characters')}`}
          </Text>
        ) : null}
      </InputWrap>
    )
  }
)

export const FormWrap = styled('form', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
})

export const InputBoxStyle = {
  background: '$inputBg',
  borderRadius: '$radii$sm',
  border: '1px solid',
  borderColor: '$inputBorder',
  outline: 'none',
}

const PrelabelTextCss = {
  '& + .MuiFormControl-root': {
    'ml': -1,
    'flexGrow': 1,
    '.MuiInputBase-root': {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  '.Mui-focused': {
    zIndex: 1,
  },
  '.MuiInputBase-root': {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
}

const InputWrapCSS = (css?: CSS | undefined) => ({
  '&.Mui': {
    '&FormControl-root': {
      marginBottom: '$space$20',
      width: '100%',
      ...(css ?? {}),
    },
  },
})

export const InputWrap = styled(FormControl, {
  '&.Mui': {
    '&FormControl-root': {
      // mb: '$space$20',
      width: '100%',
      textarea: {
        fontFamily: '$Inter',
      },
    },
  },
  '.Mui': {
    '&InputBase': {
      '&-root': {
        ...InputBoxStyle,
        transition: 'border .6s $transition$easeOutQuint',
        p: 0,
        flexGrow: 1,
      },
      '&-adornedEnd': {
        '&.iconItem': {
          input: {
            'pr': 0,
            '& ~ .iconWrap': {
              p: '$space$7 $space$12',
            },
          },
        },
      },
      '&-adornedStart': {
        '.iconWrap': {
          'p': '$space$7 $space$12',
          '& ~ input': {
            pl: 0,
            pr: '$space$14',
          },
        },
      },
    },
    '&-focused': {
      borderColor: '$inputBorderFocus',
      boxShadow: '$inputShadow',
      svg: {
        path: {
          stroke: '$gray400',
        },
      },
    },
    '&-error': {
      'borderColor': '$inputErrorBorder',
      'boxShadow': '$inputErrorShadow',
      'svg': {
        path: {
          stroke: '$error500',
        },
      },
      '&:focus': {
        borderColor: '$inputErrorBorder',
      },
    },
  },
})

export const Input = styled(InputBase, {
  'height': 'auto',
  '&.MuiInputBase-root': {
    //   '&.transparentInput input': {
    //     'backgroundColor': 'transparent',
    //     'border': 'none',
    //     'boxShadow': 'none',
    //     'color': '$primaryText',
    //     '&:disable': {
    //       cursor: 'default',
    //       backgroundColor: 'transparent',
    //       color: '$primaryText',
    //       border: 'none',
    //       borderRadius: 0,
    //     },
    //   },
    // },
    '.Mui': {
      '&InputBase': {
        '&-input': {
          'fontSize': '$14',
          'fontWeight': '$normal',
          'lineHeight': '$lineHeight$20',
          'color': '$inputText',
          'p': '$size$7 $size$12',
          'minHeight': '$size$20',
          '&::placeholder': {
            color: '$placeholder',
            fontWeight: '$normal',
          },
          '&:focus': {
            color: '$inputText',
          },
          '&:disabled': {
            'cursor': 'not-allowed',
            'backgroundColor': '$disabledBg',
            'color': '$disabledText',
            'borderColor': '$inputBorder',
            'borderRadius': '$radii$sm',
            '&::placeholder': {
              color: '$disabledText',
            },
            'svg': {
              path: {
                stroke: '$gray400',
              },
            },
          },
        },
      },
    },
  },
})
